import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import Select from '../../../components/Select';
import Tooltip from '../../../components/Tooltip';
import Chart from './Chart';
import get from 'lodash/get';
import unescape from 'lodash/unescape';
import { ADVANCED_SYMBOLS, getFeatureFlagValue } from './utils';

import './styles.css';

const messages = defineMessages({
  title: {
    id: 'hubs.scattergrams.title',
    defaultMessage: 'Scattergrams | Applications for {school}',
  },
  comparing: {
    id: 'hubs.scattergrams.comparing',
    defaultMessage: 'Comparing',
  },
  with: {
    id: 'hubs.scattergrams.with',
    defaultMessage: 'with',
  },
  unweighted: {
    id: 'hubs.scattergrams.unweighted',
    defaultMessage: 'Unweighted GPA',
  },
  weighted: {
    id: 'hubs.scattergrams.weighted',
    defaultMessage: 'Weighted GPA',
  },
  sat: {
    id: 'hubs.scattergrams.sat1600',
    defaultMessage: 'SAT (1600)',
  },
  act: {
    id: 'hubs.scattergrams.act',
    defaultMessage: 'ACT',
  },
  tooltipDescription: {
    id: 'hubs.scattergrams.tooltip-description',
    defaultMessage:
      'The highest combined SAT score takes into account the highest combined (or superscore) SAT 1600 score in accordance with the College Board test range updates from 2015-2016. In order for us to compare scores to historical SAT data, we have concorded the SAT 2400 scores to the SAT 1600 score range, to the extent applicable.',
  },
  and: {
    id: 'hubs.scattergrams.and',
    defaultMessage: 'and',
  },
  appliedTestOptional: {
    id: 'hubs.scattergrams.applied-test-optional',
    defaultMessage: 'applied test optional',
  },
});

function parseGPAs(intl) {
  const keys = {
    unweighted: 'CUMULATIVE_GPA',
    weighted: 'WEIGHTED_GPA',
  };
  return ['unweighted', 'weighted'].map((value) => ({
    text: intl.formatMessage(messages[value]),
    value: keys[value],
  }));
}

function parseTestTypes(intl, subPsat = true) {
  const keys = {
    sat: 'SAT',
    act: 'ACT',
  };
  return ['sat', 'act'].map((value) => ({
    text: intl.formatMessage(messages[value]),
    value: keys[value],
  }));
}

function getDefaultTypes(highSchool) {
  const defaultScoreType =
    Number(get(highSchool, 'hsoptions.defaultScoreType')) === 2 ? 'ACT' : 'SAT';
  const defaultGPA =
    Number(get(highSchool, 'hsoptions.defaultGPA'), 0) === 1
      ? 'WEIGHTED_GPA'
      : 'CUMULATIVE_GPA';

  return [defaultGPA, defaultScoreType];
}

function Scattergrams({
  intl,
  highSchool,
  scattergrams,
  userData,
  userInfo,
  featureFlags,
}) {
  const graphAllowGPAToggle = get(highSchool, 'options.graphAllowGPAToggle', false);
  const graphType = Number(get(highSchool, 'options.graphSymbols', 1));
  const [defaultGPA, defaultScoreType] = getDefaultTypes(highSchool);
  const gpaMessage = defaultGPA === 'WEIGHTED_GPA' ? 'weighted' : 'unweighted';
  const [gpaSelection, setGpaSelection] = useState(defaultGPA);
  const [testTypesSelection, setTestTypesSelection] = useState(defaultScoreType);
  const tooltipRef = useRef();
  const [appliedTestOptional, setAppliedTestOptional] = useState(false);
  const isTestOptionalEnabled = getFeatureFlagValue(
    featureFlags,
    'releaseSharedApplicationsTestOptional',
    false,
  );
  const includeIsTestOptionalEnabled = getFeatureFlagValue(
    featureFlags,
    'releaseNavianceStudentScattergramsIncludeIsTestOptional',
    false,
  );
  const isAdvancedSymbols = graphType === ADVANCED_SYMBOLS;
  const enableTestOptionalFeature =
    isTestOptionalEnabled && includeIsTestOptionalEnabled && isAdvancedSymbols;

  return (
    <div
      name="scattergrams"
      className="scattergrams-container common-full-width"
      data-cy="admissions-scattergram-container"
    >
      <div className="common-core-width">
        <h2 className="heading" tabIndex={0} ref={tooltipRef}>
          {intl.formatMessage(messages.title, {
            school: highSchool.name,
          })}
          <Tooltip
            className="heading-tooltip"
            contentText={intl.formatMessage(messages.tooltipDescription)}
            showCloseBtn
            onClose={() => {
              /* istanbul ignore next */
              tooltipRef.current.focus();
            }}
          >
            <i
              className="info circle icon heading-tooltip-icon"
              tabIndex="0"
              aria-label="Tooltip"
              ref={tooltipRef}
            />
          </Tooltip>
        </h2>
        <div className="filters">
          <div style={{ zIndex: 20 }}>
            <label htmlFor="compare-dropdown">
              {intl.formatMessage(messages.comparing)}
            </label>
            {graphAllowGPAToggle ? (
              <Select
                header="Select a GPA"
                data-cy="gpas-selection"
                options={parseGPAs(intl)}
                icon="chevron down"
                aria-label="GPA type"
                id="compare-dropdown"
                value={gpaSelection}
                onChange={
                  /* istanbul ignore next */ (_, item) => setGpaSelection(item.value)
                }
              />
            ) : (
              intl.formatMessage(messages[gpaMessage])
            )}
          </div>
          <div>
            <label htmlFor="sat-dropdown">{intl.formatMessage(messages.with)}</label>
            <Select
              header="Select a Test Type"
              data-cy="score-types-selection"
              options={parseTestTypes(intl)}
              icon="chevron down"
              aria-label="Test type"
              id="sat-dropdown"
              value={testTypesSelection}
              onChange={
                /* istanbul ignore next */ (_, item) => setTestTypesSelection(item.value)
              }
            />
          </div>
          {enableTestOptionalFeature && (
            <div>
              <label htmlFor="applied-test-optional-checkbox">
                {intl.formatMessage(messages.and)}
              </label>
              <input
                id="applied-test-optional-checkbox-id"
                className="applied-test-optional-checkbox"
                type="checkbox"
                onChange={(e) => setAppliedTestOptional(e.target.checked)}
              />
              <label
                className="applied-test-optional-checkbox-label"
                htmlFor="applied-test-optional-checkbox-id"
              >
                {intl.formatMessage(messages.appliedTestOptional)}
              </label>
            </div>
          )}
        </div>
        <Chart
          testType={testTypesSelection}
          gpaType={gpaSelection}
          scattergrams={scattergrams}
          userData={userData}
          userInfo={userInfo}
          graphType={graphType}
          hsOptions={get(highSchool, 'hsoptions', {})}
          featureFlags={featureFlags}
          appliedTestOptional={appliedTestOptional}
          enableTestOptionalFeature={enableTestOptionalFeature}
        />
        <div
          className="scattergrams-container-footer common-core-width"
          dangerouslySetInnerHTML={{
            __html: unescape(highSchool.graphDisclaimer),
          }}
        ></div>
      </div>
    </div>
  );
}

export default injectIntl(Scattergrams);

Scattergrams.propTypes = {
  props: PropTypes.object,
};
